import { Burger, Menu, UnstyledButton } from '@mantine/core';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import classes from './header.module.scss';

export interface HeaderProps {
    onOpenMobileNav?: () => void;
}

export const Header = ({ onOpenMobileNav }: HeaderProps) => {
    const { logout, availableTenants, selectedTenantId, setSelectedTenantId } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        window.location.href = '/login';
    };

    const handleTenantPick = (tenantId: string) => {
        setSelectedTenantId(tenantId);
        // Re-point a tenant-scoped route at the newly picked tenant so nav targets don't stay stale.
        const tenantScopedMatch = location.pathname.match(/^\/t\/[^/]+(\/.*)?$/);
        if (tenantScopedMatch) {
            navigate(`/t/${tenantId}${tenantScopedMatch[1] ?? '/backoffice'}`);
        }
    };

    const selectedTenant = availableTenants.find((tenant) => tenant.tenantId === selectedTenantId);
    const tenantLabel = selectedTenant?.companyName ?? 'בחר חברה';
    const canSwitch = availableTenants.length > 1;
    const isMonitoringRoute = location.pathname === '/monitoring';
    const monitoringSectionParam = new URLSearchParams(location.search).get('section');
    const monitoringSection = monitoringSectionParam === 'orders'
        ? 'orders'
        : monitoringSectionParam === 'kitchenPrints'
        ? 'kitchenPrints'
        : monitoringSectionParam === 'mergeLogs'
            ? 'mergeLogs'
            : monitoringSectionParam === 'approvalRequests'
                ? 'approvalRequests'
            : monitoringSectionParam === 'sessions'
                ? 'sessions'
                : monitoringSectionParam === 'kdsAudit'
                    ? 'kdsAudit'
                    : monitoringSectionParam === 'zcredit'
                        ? 'zcredit'
                        : monitoringSectionParam === 'errors'
                            ? 'errors'
                        : monitoringSectionParam === 'restigo'
                            ? 'restigo'
                        : monitoringSectionParam === 'ontopo'
                            ? 'ontopo'
                        : monitoringSectionParam === 'wolt'
                            ? 'wolt'
                        : monitoringSectionParam === 'tenbis'
                            ? 'tenbis'
                        : monitoringSectionParam === 'payments'
                            ? 'payments'
                            : 'orders';

    return (
        <header className={classes.container} data-testid="app-header">
            <div className={classes.primaryGroup}>
                <Menu position="bottom-end" shadow="md" withinPortal>
                    <Menu.Target>
                        <div className={classes.userMenuTriggers}>
                            <UnstyledButton type="button" className={classes.logoButton} aria-label="תפריט משתמש" data-testid="header-user-menu">
                                <img src="/OfficialLogo.png" alt="לוגו" className={classes.logo} />
                            </UnstyledButton>
                            <UnstyledButton
                                type="button"
                                className={`${classes.tenantButton} ${classes.tenantButtonLabel}`}
                                aria-label="תפריט משתמש"
                                data-testid="header-tenant-menu"
                            >
                                {tenantLabel}
                            </UnstyledButton>
                        </div>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {canSwitch ? (
                            <div className={classes.tenantList} data-testid="header-tenant-list">
                                {availableTenants.map((tenant) => (
                                    <Menu.Item
                                        key={tenant.tenantId}
                                        className={tenant.tenantId === selectedTenantId ? classes.tenantItemActive : undefined}
                                        onClick={() => handleTenantPick(tenant.tenantId)}
                                        data-testid={`header-tenant-${tenant.tenantId}`}
                                    >
                                        {tenant.companyName}
                                    </Menu.Item>
                                ))}
                            </div>
                        ) : (
                            <Menu.Label>{tenantLabel}</Menu.Label>
                        )}
                        <Menu.Item color="red" onClick={handleLogout} data-testid="header-logout">
                            התנתק
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </div>

            {isMonitoringRoute && (
                <div className={classes.monitoringTabs}>
                    <NavLink
                        to="/monitoring?section=orders"
                        data-testid="monitoring-tab-orders"
                        className={
                            monitoringSection === 'orders'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        הזמנות בקאופיס
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=mergeLogs"
                        data-testid="monitoring-tab-mergeLogs"
                        className={
                            monitoringSection === 'mergeLogs'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        יומן העברות ומיזוגים
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=approvalRequests"
                        data-testid="monitoring-tab-approvalRequests"
                        className={
                            monitoringSection === 'approvalRequests'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        בקשות אישור
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=payments"
                        data-testid="monitoring-tab-payments"
                        className={
                            monitoringSection === 'payments'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        תשלומים
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=kitchenPrints"
                        data-testid="monitoring-tab-kitchenPrints"
                        className={
                            monitoringSection === 'kitchenPrints'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        הדפסות מטבח
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=sessions"
                        data-testid="monitoring-tab-sessions"
                        className={
                            monitoringSection === 'sessions'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        הפעלות
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=kdsAudit"
                        data-testid="monitoring-tab-kdsAudit"
                        className={
                            monitoringSection === 'kdsAudit'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        יומן מטבח
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=zcredit"
                        data-testid="monitoring-tab-zcredit"
                        className={
                            monitoringSection === 'zcredit'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        ZCredit
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=errors"
                        data-testid="monitoring-tab-errors"
                        className={
                            monitoringSection === 'errors'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        שגיאות
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=restigo"
                        data-testid="monitoring-tab-restigo"
                        className={
                            monitoringSection === 'restigo'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        רסטיגו
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=ontopo"
                        data-testid="monitoring-tab-ontopo"
                        className={
                            monitoringSection === 'ontopo'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        מעקב אונטופו
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=wolt"
                        data-testid="monitoring-tab-wolt"
                        className={
                            monitoringSection === 'wolt'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        מעקב וולט
                    </NavLink>
                    <NavLink
                        to="/monitoring?section=tenbis"
                        data-testid="monitoring-tab-tenbis"
                        className={
                            monitoringSection === 'tenbis'
                                ? `${classes.monitoringTab} ${classes.monitoringTabActive}`
                                : classes.monitoringTab
                        }
                    >
                        מעקב תן ביס
                    </NavLink>
                </div>
            )}
            {onOpenMobileNav && (
                <span className={classes.burgerSlot}>
                    <Burger
                        opened={false}
                        onClick={onOpenMobileNav}
                        color="#ffffff"
                        size="sm"
                        aria-label="פתח תפריט ניווט"
                        data-testid="header-burger"
                    />
                </span>
            )}
            {selectedTenant?.photoUrl && (
                <span className={classes.tenantLogo}>
                    <img src={selectedTenant.photoUrl} alt={tenantLabel} data-testid="header-tenant-logo" />
                </span>
            )}
        </header>
    );
};
